import type { KPI } from "@/modules/kpi/domain/types";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { getKpiLevelLabel } from "../utils/kpi-level-labels";

interface KPIPdfPreviewContentProps {
  kpi: KPI;
}


const statusLabels: Record<string, string> = {
  active: "ກຳລັງໃຊ້ງານ",
  draft: "ຮ່າງ",
  inactive: "ປິດການໃຊ້ງານ",
};

const periodLabels: Record<string, string> = {
  monthly: "ປະຈຳເດືອນ",
  quarterly: "ປະຈຳໄຕມາດ",
  yearly: "ປະຈຳປີ",
};

export function KPIPdfPreviewContent({ kpi }: KPIPdfPreviewContentProps) {
  return (
    <div
      id="kpi-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header - Letterhead Style */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">
            Key Performance Indicator (KPI)
          </p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ເອກະສານເລກທີ:{" "}
              <span className="font-normal">{`KPI-${kpi.id.slice(-6).toUpperCase()}`}</span>
            </p>
            <p className="mt-1 font-semibold">
              ວັນທີອັບເດດລ່າສຸດ:{" "}
              <span className="font-normal">{formatDateLocal(kpi.updatedAt)}</span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* KPI Title and Status */}
      <div className="mb-12 text-center">
        <h2 className="mb-6 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          {kpi.name}
        </h2>
      </div>

      {/* Basic Information Table */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          1. ຂໍ້ມູນພື້ນຖານ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            {kpi.description && (
              <tr className="border-gray-300 border-b">
                <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ຄຳອະທິບາຍ (ວັດແທກແນວໃດ)
                </td>
                <td className="p-4 text-sm leading-relaxed">
                  {kpi.description}
                </td>
              </tr>
            )}
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ລະດັບ
              </td>
              <td className="p-4 text-sm">
                {getKpiLevelLabel(kpi.level)}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ສະຖານະ
              </td>
              <td className="p-4 text-sm">
                {statusLabels[kpi.status] || kpi.status}
              </td>
            </tr>
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີອັບເດດລ່າສຸດ
              </td>
              <td className="p-4 text-sm">{formatDateTimeLocal(kpi.updatedAt)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Measurement Method */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          2. ວິທີການວັດ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ນັບແນວໃດ (ວິທີການວັດ)
              </td>
              <td className="whitespace-pre-wrap p-4 text-sm leading-relaxed">
                {kpi.measurementMethod}
              </td>
            </tr>
            {kpi.measurementSource && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ວັດຈາກຫຍັງ (ແຫຼ່ງຂໍ້ມູນ)
                </td>
                <td className="p-4 text-sm">{kpi.measurementSource}</td>
              </tr>
            )}
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຮອບການວັດ
              </td>
              <td className="p-4 text-sm">
                {periodLabels[kpi.measurementPeriod] || kpi.measurementPeriod}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Target and Criteria */}
      {(kpi.targetValue != null || kpi.passCriteria || kpi.failCriteria) && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            3. ເປົ້າໝາຍ ແລະ ເກນການປະເມີນ
          </h3>

          <table className="mb-6 w-full border-collapse border-2 border-gray-400">
            <tbody>
              {kpi.targetValue != null && (
                <tr className="border-gray-300 border-b">
                  <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                    ຄ່າເປົ້າໝາຍ
                  </td>
                  <td className="p-4 text-sm">
                    {typeof kpi.targetValue === "number"
                      ? kpi.targetValue.toLocaleString()
                      : Number(kpi.targetValue || 0).toLocaleString()}
                  </td>
                </tr>
              )}
              {kpi.passCriteria && (
                <tr className="border-gray-300 border-b">
                  <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                    ເກນຜ່ານ
                  </td>
                  <td className="p-4 text-sm">{kpi.passCriteria}</td>
                </tr>
              )}
              {kpi.failCriteria && (
                <tr>
                  <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                    ເກນບໍ່ຜ່ານ
                  </td>
                  <td className="p-4 text-sm">{kpi.failCriteria}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes */}
      {kpi.notes && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            4. ໝາຍເຫດ
          </h3>
          <p className="whitespace-pre-line text-base text-gray-800 leading-relaxed">
            {kpi.notes}
          </p>
        </div>
      )}

      {/* Additional Information */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          {kpi.notes ? "5" : "4"}. ຂໍ້ມູນອື່ນໆ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr>
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີສ້າງ
              </td>
              <td className="p-4 text-sm">{formatDateTimeLocal(kpi.createdAt)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Footer - Signatures and Approval */}
      <div className="mt-16 border-gray-800 border-t-4 pt-8">
        <div className="grid grid-cols-2 gap-8 text-sm">
          <div>
            <p className="mb-2 font-bold text-gray-800 uppercase tracking-wide">
              ເອກະສານອະນຸມັດໂດຍ
            </p>
            <div className="mb-2 border-gray-400 border-b pb-8" />
            <p className="text-gray-600 text-xs">ຊື່-ນາມສະກຸນ, ຕໍາແໜ່ງ</p>
            <p className="text-gray-600 text-xs">ວັນທີ: _______________</p>
          </div>
          <div>
            <p className="mb-2 font-bold text-gray-800 uppercase tracking-wide">
              ເອກະສານທົບທວນໂດຍ
            </p>
            <div className="mb-2 border-gray-400 border-b pb-8" />
            <p className="text-gray-600 text-xs">ຊື່-ນາມສະກຸນ, ຕໍາແໜ່ງ</p>
            <p className="text-gray-600 text-xs">ວັນທີ: _______________</p>
          </div>
        </div>

        <div className="mt-8 border-gray-300 border-t pt-6 text-center text-gray-500 text-xs">
          <p className="font-medium">
            ເອກະສານນີ້ໄດ້ຮັບການສ້າງຈາກລະບົບຈັດການ KPI - FREEDOM Sole Co.Ltd
          </p>
          <p className="mt-2">
            ເລກເອກະສານ: {`KPI-${kpi.id.slice(-6).toUpperCase()}`} | ພິມເມື່ອ:{" "}
            {formatDateLocal(new Date())}
          </p>
        </div>
      </div>
    </div>
  );
}
